import React, { useEffect } from 'react'
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Upload,
  Button,
  Space,
  message,
} from 'antd'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { LAYOUTSECTIONTYPE, THUMBNAILSIZES } from 'src/constants'
import s from './index.module.scss'

const { TextArea } = Input

interface SectionFormModalProps {
  visible: boolean
  mode: 'add' | 'edit'
  form: any
  uploadedImage: File | null
  previewSrc: string
  onCancel: () => void
  onUpload: (info: any) => void
  onClearImage: () => void
  onSubmit: (values: any) => Promise<void>
  existingSections?: any[]
  isEditingExisting?: boolean
}

const SectionFormModal: React.FC<SectionFormModalProps> = ({
  visible,
  mode,
  form,
  uploadedImage,
  previewSrc,
  onCancel,
  onUpload,
  onClearImage,
  onSubmit,
  existingSections = [],
  isEditingExisting = false,
}) => {
  const isEdit = mode === 'edit'
  const sectionType = Form.useWatch('type', form)
  const isStatic = sectionType && sectionType !== 'DYNAMIC'

  const handleFinish = async (values) => {
    if (!uploadedImage && !form.getFieldValue('iconS3Path')) {
      message.error('Please upload an icon image before saving.')
      return
    }

    await onSubmit(values)
  }

  const circleOnly =
    sectionType === 'SUGGESTED_FRIENDS' || sectionType === 'TOP_GAMERS'

  const getThumbnailSizeOptions = () => {
    let options = []
    if (circleOnly) {
      return [{ label: 'Circle', value: 'CIRCLE' }]
    }
    options = THUMBNAILSIZES.filter((opt) => opt.value !== 'CIRCLE')
    if (isStatic) {
      options = options.filter((opt) => opt.value !== 'XLARGE')
    }
    return options
  }

  useEffect(() => {
    const current = form.getFieldValue('thumbnailSize')

    if (circleOnly) {
      form.setFieldsValue({ thumbnailSize: 'CIRCLE' })
      return
    }
    if (!circleOnly && current === 'CIRCLE') {
      form.setFieldsValue({ thumbnailSize: undefined })
    }
    if (isStatic && current === 'XLARGE') {
      form.setFieldsValue({ thumbnailSize: undefined })
    }
  }, [circleOnly, isStatic, form])

  return (
    <Modal
      title={isEdit ? 'Edit Section' : 'Add New Section'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      className={s.editSectionModal}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark="optional"
      >
        <div className={s.formColumns}>
          <div className={s.formLeft}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please enter section name' }]}
            >
              <Input placeholder="Enter section name" />
            </Form.Item>

            <Form.Item
              label="Title"
              name="title"
              rules={[
                { required: true, message: 'Please enter section title' },
              ]}
            >
              <Input placeholder="Enter section title" />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: 'Please enter description' }]}
            >
              <TextArea placeholder="Enter section description" rows={3} />
            </Form.Item>

            <Form.Item
              label="Section Type"
              name="type"
              rules={[
                { required: true, message: 'Please select section type' },
              ]}
            >
              <Select
                placeholder="Select section type"
                options={LAYOUTSECTIONTYPE}
              />
            </Form.Item>
          </div>

          <div className={s.formRight}>
            <Form.Item
              label="Thumbnail Size"
              name="thumbnailSize"
              rules={[
                { required: true, message: 'Please select thumbnail size' },
              ]}
            >
              <Select
                placeholder="Select thumbnail size"
                disabled={circleOnly}
                options={getThumbnailSizeOptions()}
              />
            </Form.Item>

            <Form.Item
              label="Status"
              name="enable"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
            </Form.Item>
            <Form.Item
              label="View All"
              name="viewAll"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
            </Form.Item>
            {form.getFieldValue('type') === 'DYNAMIC' && (
              <Form.Item
                label="Auto Shuffle"
                name="autoShuffle"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                />
              </Form.Item>
            )}
            <Form.Item label="Icon Image" required>
              <Space className={s.imageActions}>
                <Upload
                  onChange={onUpload}
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={() => false}
                >
                  <Button icon={<UploadOutlined />} size="small">
                    Upload
                  </Button>
                </Upload>
                <Button
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  onClick={onClearImage}
                >
                  Clear
                </Button>
              </Space>
            </Form.Item>
            <Form.Item name="iconS3Path" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="sectionId" hidden></Form.Item>
            {previewSrc && (
              <div className={s.imagePreview}>
                <img
                  src={previewSrc}
                  alt="Icon preview"
                  className={s.previewImage}
                />
              </div>
            )}
          </div>
        </div>

        <div className={s.modalActions}>
          <Button type="primary" htmlType="submit" className={s.saveButton}>
            {isEdit ? 'Save Changes' : 'Save Section'}
          </Button>
          <Button onClick={onCancel} className={s.cancelButton}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

export default SectionFormModal
